import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
// Import types from the new central location
import { ProcessedSchema, DbSchemaColumn } from '../types/import';

// Define the structure of the schema JSON inline for simplicity for now
// TODO: Consider fetching this from an API or a dedicated schema file
const dbSchemaJson: { tables: { [key: string]: DbSchemaColumn[] } } = {
  tables: {
    games: [
      { table_name: 'games', column_name: 'id', ordinal_position: 1, data_type: 'uuid', character_maximum_length: null, is_nullable: 'NO', column_default: 'gen_random_uuid()' },
      { table_name: 'games', column_name: 'created_at', ordinal_position: 2, data_type: 'timestamp with time zone', character_maximum_length: null, is_nullable: 'NO', column_default: 'now()' },
      { table_name: 'games', column_name: 'name', ordinal_position: 3, data_type: 'text', character_maximum_length: null, is_nullable: 'NO', column_default: null },
      { table_name: 'games', column_name: 'description', ordinal_position: 4, data_type: 'text', character_maximum_length: null, is_nullable: 'YES', column_default: null },
      { table_name: 'games', column_name: 'release_date', ordinal_position: 5, data_type: 'date', character_maximum_length: null, is_nullable: 'YES', column_default: null },
      { table_name: 'games', column_name: 'average_rating', ordinal_position: 6, data_type: 'numeric', character_maximum_length: null, is_nullable: 'YES', column_default: null },
      { table_name: 'games', column_name: 'developer_id', ordinal_position: 7, data_type: 'uuid', character_maximum_length: null, is_nullable: 'YES', column_default: null },
      { table_name: 'games', column_name: 'publisher_id', ordinal_position: 8, data_type: 'uuid', character_maximum_length: null, is_nullable: 'YES', column_default: null },
      { table_name: 'games', column_name: 'cover_url', ordinal_position: 9, data_type: 'text', character_maximum_length: null, is_nullable: 'YES', column_default: null },
      { table_name: 'games', column_name: 'short_description', ordinal_position: 10, data_type: 'text', character_maximum_length: 255, is_nullable: 'YES', column_default: null },
    ],
    // Add other tables here if needed, e.g.:
    // developers: [...],
    // publishers: [...]
  },
};

export function useImportSetup() {
  const [schema, setSchema] = useState<ProcessedSchema | null>(null);
  const [tableNames, setTableNames] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [selectedTableColumns, setSelectedTableColumns] = useState<DbSchemaColumn[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState<boolean>(true);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [identifierColumn, setIdentifierColumn] = useState<string>('');

  useEffect(() => {
    setIsLoadingSchema(true);
    setSchemaError(null);
    try {
      // Simulate async loading if schema came from API
      // In this case, we process the inline JSON directly
      const processed: ProcessedSchema = {};
      const names: string[] = [];

      if (!dbSchemaJson || !dbSchemaJson.tables) {
        throw new Error('Formato inválido para dbSchemaJson');
      }

      for (const tableName in dbSchemaJson.tables) {
        if (Object.prototype.hasOwnProperty.call(dbSchemaJson.tables, tableName)) {
          // Process columns to add inferred flags
          const columns = dbSchemaJson.tables[tableName].map((col: DbSchemaColumn) => ({
            ...col,
            // Infer PK based on common convention (name is 'id')
            isPrimaryKey: col.column_name === 'id',
            // Cannot reliably infer 'isUnique' from current schema data
            isUnique: false 
          }));
          processed[tableName] = columns;
          names.push(tableName);
        }
      }

      if (names.length === 0) {
        throw new Error('Nenhuma tabela encontrada no schema JSON');
      }

      setSchema(processed);
      setTableNames(names);
      // Automatically select the first table if available
      // setSelectedTable(names[0]);
      // Keep it empty initially to force user selection
      setSelectedTable('');
      setSelectedTableColumns([]);
      setIdentifierColumn('');

    } catch (error: any) {
      console.error("Erro ao processar schema:", error);
      setSchemaError(`Falha ao carregar o schema: ${error.message || 'Erro desconhecido'}`);
      toast.error(`Falha ao carregar o schema: ${error.message || 'Erro desconhecido'}`);
      setSchema(null);
      setTableNames([]);
      setSelectedTable('');
      setSelectedTableColumns([]);
      setIdentifierColumn('');
    } finally {
      setIsLoadingSchema(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTable && schema) {
      setSelectedTableColumns(schema[selectedTable] || []);
      setIdentifierColumn('');
    } else {
      setSelectedTableColumns([]);
    }
  }, [selectedTable, schema]);

  const handleTableChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTable(event.target.value);
  }, []);

  const handleIdentifierChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setIdentifierColumn(event.target.value);
  }, []);

  return {
    schema,
    tableNames,
    selectedTable,
    selectedTableColumns,
    identifierColumn,
    handleTableChange,
    handleIdentifierChange,
    isLoadingSchema,
    schemaError,
  };
} 